import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Hold-to-talk for the phones that cannot listen for themselves.
 *
 * Apple's dictation stack has no Lithuanian in it, so on an iPhone the browser's
 * own recogniser is not slow or unreliable — it is absent, whatever the presence
 * of `webkitSpeechRecognition` suggests. The alternative is to record what was
 * said and let the model that already reads the transcript do the listening too.
 *
 * The same shape as `useSpeech` on purpose: hold, release, and one finished
 * result once. What differs is where it goes — a recording leaves the phone,
 * where a transcript from the on-device engine already had. On Android the
 * engine sends the audio to Google either way; this only makes that explicit.
 */

/**
 * Long enough for "dvi riekės duonos su sviestu ir puodelis kavos", short enough
 * that a button held down in a pocket cannot upload a podcast.
 */
const MAX_SECONDS = 30

/** Under this the clip holds a click and a breath, not a sentence. */
const MIN_BYTES = 2048

/**
 * What the recorder should produce, best first.
 *
 * iOS Safari can only do MP4/AAC; Chrome can only do WebM/Opus; Firefox prefers
 * Ogg. Asking in this order means each browser gets the one container it can
 * actually write, rather than a silent failure from `new MediaRecorder`.
 */
const FORMATS = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']

function pickFormat(): string | undefined {
  const supported = (window as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder
  if (!supported?.isTypeSupported) return undefined
  return FORMATS.find((type) => supported.isTypeSupported(type))
}

export function recorderAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    'MediaRecorder' in window &&
    navigator.mediaDevices?.getUserMedia != null
  )
}

/**
 * Codec parameters are part of a browser's MIME string and not part of the
 * model's: `audio/webm;codecs=opus` is rejected where `audio/webm` is read.
 */
export function baseMimeType(mimeType: string): string {
  return (mimeType.split(';')[0] ?? mimeType).trim().toLowerCase()
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error ?? new Error('read failed'))
    reader.onload = () => {
      const url = String(reader.result)
      const comma = url.indexOf(',')
      resolve(comma < 0 ? '' : url.slice(comma + 1))
    }
    reader.readAsDataURL(blob)
  })
}

export interface VoiceClip {
  mimeType: string
  base64: string
}

export interface RecorderState {
  supported: boolean
  listening: boolean
  error: string | null
}

export function useRecorder(onClip: (clip: VoiceClip) => void) {
  const recorder = useRef<MediaRecorder | null>(null)
  const chunks = useRef<Blob[]>([])
  const timer = useRef<number | null>(null)
  /**
   * Whether the button is still held.
   *
   * Asking for the microphone is asynchronous and, the first time, waits on a
   * permission dialog — which is always longer than the press that triggered it.
   * Without this the release lands on nothing and the stream opens afterwards,
   * leaving the phone recording with its indicator lit and nobody talking.
   */
  const held = useRef(false)
  // The caller re-renders on every state change; holding the callback in a ref
  // keeps `start` stable so a re-render mid-sentence cannot restart the mic.
  const clipHandler = useRef(onClip)
  clipHandler.current = onClip

  const [state, setState] = useState<RecorderState>(() => ({
    supported: recorderAvailable(),
    listening: false,
    error: null,
  }))

  /**
   * Put the current recorder down and disown it completely.
   *
   * Detaching the handlers is the load-bearing part, not the track stopping.
   * Stopping the last track of a stream ends the recording, so a recorder left
   * with its handlers attached fires `stop` *after* the next one has been
   * created — and that stale handler writes to the same refs, clearing the new
   * recorder's chunks, killing its timer and stopping its tracks while the user
   * is still holding the button. Tracks left running also keep the phone's
   * recording indicator lit, which is its own reason to be thorough here.
   */
  const release = useCallback(() => {
    if (timer.current != null) window.clearTimeout(timer.current)
    timer.current = null
    held.current = false
    const previous = recorder.current
    recorder.current = null
    if (!previous) return
    previous.ondataavailable = null
    previous.onstop = null
    previous.onerror = null
    if (previous.state !== 'inactive') previous.stop()
    for (const track of previous.stream.getTracks()) track.stop()
  }, [])

  const stop = useCallback(() => {
    held.current = false
    // `stop` flushes the last chunk and fires onstop; there is no discard here,
    // because releasing the button means "I have finished saying it".
    if (recorder.current?.state === 'recording') recorder.current.stop()
    else setState((s) => ({ ...s, listening: false }))
  }, [])

  /**
   * Throw the recording away — the opposite of `stop`.
   *
   * A sheet dismissed mid-sentence, or the scan rail moved off Balsu, is not
   * somebody who finished saying it. Stopping would flush the clip and spend a
   * model call on a conversation nobody was having, so the handlers come off
   * first and only then is the microphone put down.
   */
  const cancel = useCallback(() => {
    chunks.current = []
    release()
    setState((s) => (s.listening || s.error != null ? { ...s, listening: false, error: null } : s))
  }, [release])

  const start = useCallback(async () => {
    if (!recorderAvailable()) return
    release()
    chunks.current = []
    held.current = true
    setState((s) => ({ ...s, error: null }))

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      // An installed iOS web app asks for the microphone again every launch, so
      // this is a normal state to be in rather than a broken one.
      held.current = false
      setState((s) => ({ ...s, listening: false, error: 'Mikrofono prieiga atmesta' }))
      return
    }

    // Released while the permission dialog was up: nothing was said, and the
    // stream must be handed straight back rather than left open.
    if (!held.current) {
      for (const track of stream.getTracks()) track.stop()
      setState((s) => ({ ...s, listening: false }))
      return
    }

    const mimeType = pickFormat()
    const instance = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    recorder.current = instance

    instance.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.current.push(event.data)
    }
    instance.onerror = () => {
      release()
      setState((s) => ({ ...s, listening: false, error: 'Įrašyti nepavyko' }))
    }
    instance.onstop = () => {
      const type = instance.mimeType || mimeType || 'audio/webm'
      const blob = new Blob(chunks.current, { type })
      chunks.current = []
      release()
      setState((s) => ({ ...s, listening: false }))
      if (blob.size < MIN_BYTES) {
        setState((s) => ({ ...s, error: 'Nieko negirdėti' }))
        return
      }
      void toBase64(blob).then((base64) => {
        if (base64) clipHandler.current({ mimeType: baseMimeType(type), base64 })
      })
    }

    instance.start()
    setState((s) => ({ ...s, listening: true }))
    timer.current = window.setTimeout(stop, MAX_SECONDS * 1000)
  }, [release, stop])

  useEffect(() => release, [release])

  return { ...state, start, stop, cancel }
}
